// ==============================|| COLOR GRADIENT GENERATOR ||============================== //

/**
 * Converte cor hex para RGB
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : null;
}

/**
 * Converte RGB para hex
 */
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

/**
 * Ajusta o brilho de uma cor RGB
 * @param rgb - Objeto RGB
 * @param factor - Fator de ajuste (-1 a 1, onde -1 = preto, 0 = sem mudança, 1 = branco)
 */
function adjustBrightness(
  rgb: { r: number; g: number; b: number },
  factor: number
): { r: number; g: number; b: number } {
  return {
    r: Math.max(0, Math.min(255, Math.round(rgb.r + (255 - rgb.r) * factor))),
    g: Math.max(0, Math.min(255, Math.round(rgb.g + (255 - rgb.g) * factor))),
    b: Math.max(0, Math.min(255, Math.round(rgb.b + (255 - rgb.b) * factor)))
  };
}

/**
 * Gera um array de cores baseado em uma cor base
 * @param baseColor - Cor base em formato hex (#RRGGBB)
 * @param isDark - Se é para tema escuro
 * @returns Array de 10 variações da cor
 */
export function generateColorGradient(baseColor: string, isDark: boolean = false): string[] {
  const rgb = hexToRgb(baseColor);
  if (!rgb) {
    // Fallback para cor padrão se inválida
    return [
      '#E9F0FF',
      '#C8D9FF',
      '#A3C0FF',
      '#7EA6FF',
      '#6293FF',
      '#4680FF',
      '#3F78FF',
      '#376DFF',
      '#2F63FF',
      '#2050FF'
    ];
  }

  const colors: string[] = [];

  if (isDark) {
    // Para tema escuro: do mais escuro ao mais claro, gradiente mais claro
    for (let i = 0; i < 10; i++) {
      const factor = i * 0.08; // 0 a 0.72
      const adjustedRgb = adjustBrightness(rgb, factor);
      colors.push(rgbToHex(adjustedRgb.r, adjustedRgb.g, adjustedRgb.b));
    }
  } else {
    // Para tema claro: do mais claro ao mais escuro, gradiente mais claro
    for (let i = 0; i < 10; i++) {
      const factor = (9 - i) * 0.1; // 0.72 a 0
      const adjustedRgb = adjustBrightness(rgb, factor);
      colors.push(rgbToHex(adjustedRgb.r, adjustedRgb.g, adjustedRgb.b));
    }
  }

  return colors;
}

/**
 * Gera gradiente de cores secundárias (tons de cinza personalizados)
 * @param baseColor - Cor base para derivar os cinzas
 * @param isDark - Se é para tema escuro
 * @returns Array de 10 variações de cinza
 */
export function generateSecondaryGradient(baseColor: string, isDark: boolean = false): string[] {
  const rgb = hexToRgb(baseColor);
  if (!rgb) {
    // Fallback padrão
    return isDark
      ? ['#131920', '#1D2630', '#3E4853', '#5B6B79', '#8996A4', '#BEC8D0', '#DBE0E5', '#F3F5F7', '#F8F9FA', '#F8F9FA']
      : ['#F8F9FA', '#F8F9FA', '#F3F5F7', '#DBE0E5', '#BEC8D0', '#8996A4', '#5B6B79', '#3E4853', '#1D2630', '#131920'];
  }

  // Calcula luminância da cor base para criar cinzas harmoniosos
  // const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;

  const colors: string[] = [];

  if (isDark) {
    // Tons escuros para tema escuro
    const baseGrays = [0.1, 0.16, 0.24, 0.36, 0.52, 0.68, 0.8, 0.9, 0.95, 0.98]; // menos claros
    baseGrays.forEach((gray) => {
      const grayValue = Math.round(gray * 255);
      // Reduz matiz para menos opacidade
      const tintedR = Math.round(grayValue + (rgb.r - grayValue) * 0.03);
      const tintedG = Math.round(grayValue + (rgb.g - grayValue) * 0.03);
      const tintedB = Math.round(grayValue + (rgb.b - grayValue) * 0.03);
      colors.push(
        rgbToHex(
          Math.max(0, Math.min(255, tintedR)),
          Math.max(0, Math.min(255, tintedG)),
          Math.max(0, Math.min(255, tintedB))
        )
      );
    });
  } else {
    // Tons claros para tema claro
    const baseGrays = [0.96, 0.94, 0.9, 0.8, 0.68, 0.5, 0.34, 0.22, 0.16, 0.1]; // menos claros
    baseGrays.forEach((gray) => {
      const grayValue = Math.round(gray * 255);
      // Reduz matiz para menos opacidade
      const tintedR = Math.round(grayValue + (rgb.r - grayValue) * 0.02);
      const tintedG = Math.round(grayValue + (rgb.g - grayValue) * 0.02);
      const tintedB = Math.round(grayValue + (rgb.b - grayValue) * 0.02);
      colors.push(
        rgbToHex(
          Math.max(0, Math.min(255, tintedR)),
          Math.max(0, Math.min(255, tintedG)),
          Math.max(0, Math.min(255, tintedB))
        )
      );
    });
  }

  return colors;
}

/**
 * Escurece uma cor RGB em direção ao preto
 * @param rgb - Objeto RGB
 * @param amount - Fração a escurecer (0 a 1)
 */
function darkenRgb(rgb: { r: number; g: number; b: number }, amount: number): { r: number; g: number; b: number } {
  return {
    r: Math.max(0, Math.round(rgb.r * (1 - amount))),
    g: Math.max(0, Math.round(rgb.g * (1 - amount))),
    b: Math.max(0, Math.round(rgb.b * (1 - amount)))
  };
}

/**
 * Gera cores de status baseadas na cor primária do tenant
 * @param primaryColor - Cor primária do tenant
 * @param isDark - Se é para tema escuro
 * @returns Objeto com cores de erro, aviso, info e sucesso
 */
export function generateStatusColors(primaryColor: string, isDark: boolean = false) {
  // Cores base para status (hex fixo, sem opacidade)
  const statusBase = {
    error: '#DF1F1F', // Vermelho
    warning: '#EDC01C', // Laranja
    info: '#3EC9D6', // Azul claro
    success: '#19B17E' // Verde
  };

  function getStatusVariants(color: string) {
    const rgb = hexToRgb(color);
    if (!rgb) {
      return { lighter: color, light: color, main: color, dark: color, darker: color };
    }

    const pale = adjustBrightness(rgb, isDark ? 0.15 : 0.75);
    const mid = adjustBrightness(rgb, isDark ? 0.06 : 0.4);
    const dark = darkenRgb(rgb, 0.15);
    const darker = darkenRgb(rgb, 0.35);

    const paleHex = rgbToHex(pale.r, pale.g, pale.b);
    const midHex = rgbToHex(mid.r, mid.g, mid.b);
    const mainHex = rgbToHex(rgb.r, rgb.g, rgb.b);
    const darkHex = rgbToHex(dark.r, dark.g, dark.b);
    const darkerHex = rgbToHex(darker.r, darker.g, darker.b);

    // em modo escuro, o fundo do chip precisa ficar próximo da cor base (não sumir no card escuro),
    // e o texto precisa ficar claro — por isso os tons de "lighter"/"darker" se invertem, igual ao tema estático
    return isDark
      ? { lighter: darkerHex, light: darkHex, main: mainHex, dark: midHex, darker: paleHex }
      : { lighter: paleHex, light: midHex, main: mainHex, dark: darkHex, darker: darkerHex };
  }

  return {
    error: getStatusVariants(statusBase.error),
    warning: getStatusVariants(statusBase.warning),
    info: getStatusVariants(statusBase.info),
    success: getStatusVariants(statusBase.success)
  };
}
